import { extractPdfStatement } from "@/features/banking/pdf-statement";

type ScannedReceipt = {
  fileName: string;
  text: string;
  image?: string;
};

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;

function boundedImageScale(width: number, height: number): number {
  if (!Number.isFinite(width * height) || width <= 0 || height <= 0) {
    throw new Error("Invalid receipt image dimensions.");
  }
  return Math.min(1, 4096 / width, 4096 / height, Math.sqrt(2_000_000 / (width * height)));
}

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new Error("The receipt image could not be read."));
    reader.readAsDataURL(file);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("The receipt image could not be read."));
    image.src = url;
  });
}

/** OCR one receipt (PDF or image) on this device. Nothing is uploaded. */
export async function extractReceiptFile(
  file: File,
  signal: AbortSignal,
  progress: (message: string) => void,
): Promise<ScannedReceipt> {
  if (file.size > MAX_RECEIPT_BYTES) {
    throw new Error("The receipt exceeds the 10 MiB limit.");
  }
  const isPdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (isPdf) {
    const pages = await extractPdfStatement(file, signal, progress);
    return {
      fileName: file.name,
      text: pages.map((page) => page.text).join("\n"),
      image: pages[0]?.image,
    };
  }
  if (!file.type.startsWith("image/")) {
    throw new Error("Use a PDF, PNG, or JPEG receipt.");
  }
  progress("Reading the receipt on this device…");
  // CSP allows `data:` images but not `blob:`; keep the file in memory only.
  const url = await readDataUrl(file);
  let ocr: import("tesseract.js").Worker | undefined;
  try {
    const image = await loadImage(url);
    signal.throwIfAborted();
    const scale = boundedImageScale(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.floor(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The receipt image could not be read.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    progress("Recognizing the receipt on this device…");
    const base = new URL(import.meta.env.BASE_URL, window.location.origin);
    const { createWorker } = await import("tesseract.js");
    ocr = await createWorker("eng", 1, {
      workerPath: new URL("ocr/worker.min.js", base).href,
      corePath: new URL("ocr/core/", base).href,
      langPath: new URL("ocr/", base).href,
      workerBlobURL: false,
      cacheMethod: "none",
    });
    signal.throwIfAborted();
    const result = await ocr.recognize(canvas);
    return {
      fileName: file.name,
      text: result.data.text.trim(),
      image: canvas.toDataURL("image/png"),
    };
  } finally {
    await ocr?.terminate().catch(() => undefined);
  }
}
